/**
 * walkHereCommand — the one command registered on the viewport right-click menu
 * (ENG-1050): plan a navmesh route from the avatar to the clicked ground point,
 * generate a walk with kimodo, play it.
 *
 * The impure half of the feature. Planning maths live in walkHerePlan.js (pure,
 * unit-tested); the phase machine lives in walkHereState.js (pure, unit-tested);
 * this module is the orchestration that touches the network and the mixer, and
 * is covered by tests/e2e/ci/walk-here-menu.spec.js.
 *
 * KIMODO ONLY, by decision. There is deliberately no fallback to
 * movement.moveToLocation (ENG-592's instant navmesh walk): a generation failure
 * surfaces as a visible error, never as a different kind of motion the user
 * didn't ask for and can't tell apart from the real thing.
 *
 * Every phase is visible (WalkHereIndicator + WalkHereRoute). Generation takes
 * seconds, so the route preview is drawn BEFORE the request goes out — the click
 * always produces an immediate visible result even though the walk cannot.
 */
import { genBodyMotionOnWaypoints, currentAvatarXZ, currentAvatarFacingXZ } from './bodyMotionNav'
import { planWalkHere } from './walkHerePlan.js'
import { DEFAULT_SMOOTH, clampRouteDuration, routeHeadingPreview } from './bodyMotionPath.js'
import { resolveAvatarTransformTarget } from './avatarTransformTarget.js'
import { registerViewportCommand } from './viewportCommands.js'
import { walkHereState } from './walkHereState.js'
import { DEFAULT_WALK_PROMPT, walkHereSettings } from './walkHereSettings.js'

/** The default prompt, re-exported from where the editable settings live so
 *  there is one definition of "what a walk-here asks for by default". */
export const WALK_PROMPT = DEFAULT_WALK_PROMPT

/** How long the "Arrived" beat holds before the pill fades (mirrors
 *  TextureUpgradeIndicator's DONE_HOLD_MS rationale: an instant completion that
 *  blinks out reads as a glitch, not as success). */
export const ARRIVED_HOLD_MS = 1600

/** Route shape for a walk-here click. Deliberately the library default rather
 *  than a value of its own: a right-click walk and a wizard route are the same
 *  journey planned the same way, so the two must not drift into looking
 *  different. The clip and the previewed pill route are planned by two separate
 *  calls (see runWalkHere), and both read this. */
export const WALK_SMOOTH = DEFAULT_SMOOTH

/**
 * The avatar's current world position. The group is what root motion actually
 * translates (and what the movement component drives), so it is the authoritative
 * "where the walk starts"; the pelvis world position is the fallback for a rig
 * mounted without the group global. Both throw rather than defaulting to the
 * origin — a walk planned from [0,0,0] would look like a teleport.
 */
export function avatarWorldPosition() {
  // Whichever avatar is mounted — reading __AVATAR_GROUP__ directly is what made every
  // walk-here click on a splat character report 'avatar not ready'.
  const g = resolveAvatarTransformTarget()
  if (g?.position) return [g.position.x, g.position.y, g.position.z]
  return currentAvatarXZ() // throws 'avatar not ready (no pelvis)'
}

/** Read the live navmesh. Null is a REASON, not a fallback to a straight line. */
function liveNavmesh() {
  return (typeof window !== 'undefined' && window.__NAVMESH_INSTANCE__) || null
}

/**
 * Plan the click, caching the result on the per-open ctx so `run` doesn't re-plan
 * (and so the label the user read is the route they get).
 * @returns {{plan:object}|{error:string}}
 */
export function resolveWalkHere(ctx) {
  if (ctx._walkHere) return ctx._walkHere
  let result
  try {
    let from
    try {
      from = avatarWorldPosition()
    } catch (err) {
      throw new Error(String(err?.message ?? err))
    }
    result = { plan: planWalkHere({ navmesh: ctx.navmesh ?? liveNavmesh(), from, to: ctx.point, smooth: ctx.smooth ?? WALK_SMOOTH }) }
  } catch (err) {
    result = { error: String(err?.message ?? err) }
  }
  ctx._walkHere = result
  return result
}

/**
 * Run the walk. Sequential by design — one generated clip owns __DIRECT_PLAY__ at
 * a time, and the store's requestId means a second click supersedes the first
 * rather than two pills fighting over the phase.
 */
export async function runWalkHere(ctx) {
  const resolved = resolveWalkHere(ctx)
  if (resolved.error) {
    // Reachable if the menu is driven programmatically; a disabled entry can't be
    // clicked. Fail loudly rather than returning a quiet no-op.
    const id = walkHereState.start({ route: null, destination: null, distance: 0, clamped: false })
    walkHereState.fail(id, resolved.error)
    return null
  }
  const { waypoints, route, distance, clamped, destination } = resolved.plan
  // The editable half. Defaults to exactly the shipped prompt and speed, so an
  // untouched install builds the request it always did.
  const { prompt, walkSpeed } = walkHereSettings.getState()

  // Preview first: the click is answered on screen before the network call.
  //
  // The route carries the anchors the request will hold it to — every root_xz
  // point and the facing asked for there — built from the SAME defaults the
  // generation below uses, so the dots on screen are the dots kimodo receives.
  // A preview that quietly used different numbers would be worse than none.
  let headings = null
  try {
    headings = routeHeadingPreview(route.path, waypoints, {
      durationSec: clampRouteDuration(distance, walkSpeed),
      startFacing: currentAvatarFacingXZ(),
    })
  } catch (err) {
    // Never let drawing the preview cost the walk itself.
    console.warn('[walkHere] heading preview failed:', err)
  }
  const id = walkHereState.start({
    route: headings ? { ...route, headings } : route,
    destination, distance, clamped,
  })
  walkHereState.advance(id, 'generating', `${distance.toFixed(1)} m`)

  let res
  try {
    res = await genBodyMotionOnWaypoints({
      prompt,
      waypoints,
      pathMode: 'navmesh',
      walk_speed: walkSpeed,
      // The plan's own mode, not the default: the route drawn on screen and the
      // route sent to kimodo are planned by two separate calls, and a mismatch
      // would show the user a line the avatar then doesn't walk.
      smooth: resolved.plan.smooth,
    })
  } catch (err) {
    console.error('[walkHere] generation failed:', err)
    walkHereState.fail(id, err?.message ?? err)
    return null
  }

  // A superseded request must not take over playback reporting.
  if (!walkHereState.advance(id, 'walking', `${distance.toFixed(1)} m`)) return res

  // The clip self-clears via __DIRECT_PLAY__'s own expiry; mirror that timing for
  // the pill rather than polling the render loop from here.
  const holdMs = Math.max(0, (res.durationSec ?? 0) * 1000)
  setTimeout(() => {
    if (!walkHereState.advance(id, 'arrived', res.reachedGoal === false ? 'stopped short' : null)) return
    setTimeout(() => {
      if (walkHereState.getState().requestId === id) walkHereState.reset()
    }, ARRIVED_HOLD_MS)
  }, holdMs)

  return res
}

if (typeof window !== 'undefined') {
  // Console/test seam: walk to an exact world point without hunting for the
  // pixel that raycasts there — `__WALK_HERE_RUN__([3, 0, 2])`. Runs the SAME
  // orchestrator the menu entry does, so a spec exercises production code rather
  // than a parallel path.
  window.__WALK_HERE_RUN__ = (point) => runWalkHere({ point })
}

registerViewportCommand({
  id: 'walkHere',
  label: 'Walk here',
  order: 10,
  resolve(ctx) {
    const r = resolveWalkHere(ctx)
    if (r.error) return { enabled: false, reason: r.error }
    // A walk generated from an edited prompt or speed behaves differently from
    // the shipped one, and there is nothing else on screen to say so — the
    // marker puts that on the row BEFORE the click rather than leaving it to be
    // inferred from a result that looks wrong.
    const edited = !walkHereSettings.isDefault()
    const s2 = walkHereSettings.getState()
    return {
      enabled: true,
      // Say it in the label: a clamped route walks part of the way, and finding
      // that out after the generation would read as the model ignoring the click.
      label: r.plan.clamped ? 'Walk here (nearest reachable)' : 'Walk here',
      badge: edited ? '✳' : null,
      badgeTitle: edited
        ? `Edited: "${s2.prompt}" at ${s2.walkSpeed} m/s — open the arrow to change or reset`
        : null,
    }
  },
  run: runWalkHere,
})
